/**
 * Reading a value that is really a word.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS IS NOT `labels[value]`
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Three things make the obvious one-liner wrong, and each is a bug that would reach a screen
 * looking plausible:
 *
 * 1. THE KEY IS THE RAW VALUE, NOT THE CONVERTED ONE. `LatestRow.value` is already
 *    `raw * scale + offset`. A register whose scale is 0.1 reports raw 100 and value 10;
 *    looking up "10" in a map keyed on "100" misses, and the screen shows nothing for a
 *    reading that arrived perfectly well. An enumeration should never carry a scale, but
 *    `scale` defaults to 1 for unclassified metrics and an admin can set one by mistake — so
 *    this reads `raw`, deliberately.
 *
 * 2. A VALUE WITH NO ENTRY MUST STILL RENDER. Maps are filled in as somebody learns the
 *    hardware, so a device will report a state nobody has labelled yet. `undefined` rendered
 *    into JSX is nothing at all — the cell goes blank and an operator concludes the device
 *    stopped reporting. An unlabelled value shows AS ITSELF and is marked unrecognised.
 *
 * 3. INTEGER KEYS ONLY. JSON keys are text and the value is a double. Integers are the only
 *    sane key space for an enumeration, so a non-integer is treated as unlabelled rather
 *    than coerced into a near-miss.
 */
import type { LatestRow } from '@/lib/api/types';

export interface LabelledValue {
  /** What to show. Never empty when the row has a value. */
  readonly text: string;
  /** True when a label was found. False means `text` is the number itself. */
  readonly labelled: boolean;
  /** True when the metric IS an enumeration but this value has no entry. */
  readonly unknownState: boolean;
  /** 'bool' | 'enum' | null — for choosing a chip versus a toggle. */
  readonly kind: string | null;
}

/** Does this reading represent a state rather than a quantity? */
export function isEnumerated(row: Pick<LatestRow, 'labels'>): boolean {
  return hasLabels(row.labels);
}

/** A label map with at least one entry. `{}` is no labels — the column has held it. */
export function hasLabels(labels: Readonly<Record<string, string>> | null | undefined): labels is Readonly<Record<string, string>> {
  return labels !== null && labels !== undefined && Object.keys(labels).length > 0;
}

/**
 * The word for one CODE under one label map, or null when the map does not apply.
 *
 * The one lookup every surface shares — the latest cell (`valueLabel`), a chart's readout and
 * table, the Parameters tables, a derived reading's own panel — for a measured reading and a
 * derived one alike (Hamzah, 2026-10-13: "in whole app it should be connected"). A surface that
 * wrote its own `labels[value]` would be the one that forgot note 3 below and printed "Running"
 * for a 1.5.
 *
 * Null when there are no labels or the value is not a whole number: the caller prints the
 * number as it always did. A labelled reading whose code has no entry comes back as the code
 * itself with `unknownState` — shown, never blank (note 2).
 */
export function codeWord(
  labels: Readonly<Record<string, string>> | null | undefined,
  code: number | string | null | undefined,
): { text: string; labelled: boolean; unknownState: boolean } | null {
  if (!hasLabels(labels)) return null;
  if (code === null || code === undefined || code === '') return null;
  const n = typeof code === 'number' ? code : Number(code);
  if (!Number.isFinite(n) || !Number.isInteger(n)) return null;
  const hit = labels[String(n)];
  if (hit !== undefined && hit !== '') return { text: hit, labelled: true, unknownState: false };
  return { text: String(n), labelled: false, unknownState: true };
}

/**
 * The operator-facing reading of one row.
 *
 * Quantities come back unchanged, so this is safe to call on anything and callers need not
 * branch first — which is what stops one surface forgetting to.
 */
export function valueLabel(
  row: Pick<LatestRow, 'labels' | 'labelKind' | 'value' | 'raw' | 'valueText'>,
): LabelledValue {
  const kind = row.labelKind;

  if (!isEnumerated(row)) {
    const text = row.value !== null ? String(row.value) : (row.valueText ?? '—');
    return { text, labelled: false, unknownState: false, kind: null };
  }

  /* The raw register value is the key — see note 1. Fall back to the converted value only
     when raw is absent, which happens for a text-valued series. */
  const key = row.raw ?? row.value;
  if (key === null || !Number.isFinite(key) || !Number.isInteger(key)) {
    return {
      text: row.value !== null ? String(row.value) : (row.valueText ?? '—'),
      labelled: false,
      unknownState: key !== null,
      kind,
    };
  }

  /*
   * Labelled metric, unlabelled value: `codeWord` shows the number and says it is unrecognised —
   * a prompt to go and label it, and a genuinely useful signal, because a state nobody has seen
   * before is often the interesting one.
   */
  const word = codeWord(row.labels, key);
  return word === null
    ? { text: String(key), labelled: false, unknownState: true, kind }
    : { ...word, kind };
}

/**
 * Whether a labelled reading is the "good" state.
 *
 * Returns null in the common case, on purpose. This exists so callers stop inferring
 * severity from the NUMBER: 0 is not reliably good and 1 is not reliably bad, and both
 * conventions appear on the same site in this fleet. The only defensible source of alarm is
 * an alarm. `warn` is returned solely for a state the platform does not recognise.
 */
export function labelTone(v: LabelledValue): 'warn' | null {
  return v.unknownState ? 'warn' : null;
}
